import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save,
  Info,
} from '@mui/icons-material';
import { systemSettingService } from '../../services/systemSettingService';

const ApprovalSettings: React.FC = () => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    requireInvoiceApproval: false,
    requireEmployeeApproval: false,
    requireRequestApproval: false,
    requirePaymentApproval: false,
    requirePurchaseOrderApproval: false,
    requireQuotationApproval: false,
    requireSalesOrderApproval: false,
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await systemSettingService.getApprovalSettings();
        const loadedSettings = response.data?.data || response.data || {};
        setSettings({
          requireInvoiceApproval: loadedSettings.requireInvoiceApproval || false,
          requireEmployeeApproval: loadedSettings.requireEmployeeApproval || false,
          requireRequestApproval: loadedSettings.requireRequestApproval || false,
          requirePaymentApproval: loadedSettings.requirePaymentApproval || false,
          requirePurchaseOrderApproval: loadedSettings.requirePurchaseOrderApproval || false,
          requireQuotationApproval: loadedSettings.requireQuotationApproval || false,
          requireSalesOrderApproval: loadedSettings.requireSalesOrderApproval || false,
        });
      } catch (error) {
        console.error('Error loading approval settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleToggle = (setting: string) => {
    setSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting as keyof typeof prev],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await systemSettingService.updateApprovalSettings(settings);
      alert('Approval settings saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save approval settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>{t('approvals.settings')}</Typography>

        <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
          {t('approvals.autoApproveNote')}
        </Alert>

        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>{t('approvals.needApprove')}</Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Box>
                <FormControlLabel
                  control={<Switch checked={settings.requireInvoiceApproval} onChange={() => handleToggle('requireInvoiceApproval')} />}
                  label={t('approvals.invoices')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {t('approvals.requireApprovalInvoice')}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                  control={<Switch checked={settings.requireEmployeeApproval} onChange={() => handleToggle('requireEmployeeApproval')} />}
                  label={t('approvals.employees')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {t('approvals.requireApprovalEmployee')}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                  control={<Switch checked={settings.requireRequestApproval} onChange={() => handleToggle('requireRequestApproval')} />}
                  label={t('approvals.requests')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {t('approvals.requireApprovalRequest')}
                </Typography>
              </Box>

              <Box>
                <FormControlLabel
                  control={<Switch checked={settings.requirePaymentApproval} onChange={() => handleToggle('requirePaymentApproval')} />}
                  label={t('approvals.payments')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {t('approvals.requireApprovalPayment')}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                  control={<Switch checked={settings.requirePurchaseOrderApproval} onChange={() => handleToggle('requirePurchaseOrderApproval')} />}
                  label={t('approvals.purchaseOrders')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {t('approvals.requireApprovalPO')}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                  control={<Switch checked={settings.requireQuotationApproval} onChange={() => handleToggle('requireQuotationApproval')} />}
                  label={t('approvals.quotations')}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {t('approvals.requireApprovalQuotation')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                {saving ? t('common.saving') : t('approvals.saveSettings')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
  );
};

export default ApprovalSettings;
